import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Container, ProgressBar } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import IssuerLogo from '../../../assets/Issuer.svg';
import CustomTable from '../../../components/CustomTable/CustomTable';
import RoundedCheckbox from '../../../components/Input/RadioButton';
import Select, { BooleanishOptions } from '../../../components/Input/Select';
import NavigationFooter from '../../../components/NavigationFooter/NavigationFooter';
import { businessSectorService, BusinessSectorTableResponse } from '../../../services/business-sector-table';
import { getBusinessPlansTableData, updateConditionsTariff } from '../../../services/commerce.service';
import { valuesConfigurationService } from '../../../services/values-configuration.service';
import { ConditionsAndTariffLocationState, CustomTableChunk } from '../../../types/add_commerce.types';
import { UpdateConditionsAndTariffsRequest } from '../../../types/business/request';
import { BusinessSectorEnum } from '../../../types/enums/business-sector.enum';
import { ConditionsTariffEnum } from '../../../types/enums/conditions-tariff.enum';
import { TariffRateType, TariffType } from '../../../types/enums/tariff.enum';
import { ValuesConfigurationName } from '../../../types/enums/values-configuration.enum';
import {
  currencies,
  masterManualComisionHeadings,
  masterPersonalizedComisionHeadings,
  personalizedTariffMasterDataDefault,
  personalizedTariffVisaDataDefault,
  smallBusinessDescription,
  standarComisionHeadings,
  visaManualComisionHeadings,
  visaPersonalizedComisionHeadings,
} from '../../../utils/data';
import {
  addPercentage,
  capitalize,
  formatTableDataForTariffsMaster,
  formatTableDataForTariffsVisa,
  getEnumTypeValue,
  triggerToasts,
} from '../../../utils/helpers';
import { pagesPaths } from '../../../utils/navigationUtils';
import { getProgressPercentage } from '../../../utils/progressHelper';
import schemas from '../../../utils/schemas';
import {
  checkCanAccessPersonalizedTariff,
  checkCanUseManualTariff,
  getBankAccountsCurrencyToOperate,
  getPersonalizedTariffsFromSector,
  parsePersonalizedTariffsSentToNumbers,
  updatePersonalizedTariffs,
} from '../../../utils/tariffHelper';
import PersonalaizedTariffTable from './PersonalaizedTariff';

function CommerceConditions() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const managerHasTariffRestriction = localStorage.getItem('tariffRestriction') === 'true';
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<FormikProps<any> | null>(null);

  const localState: ConditionsAndTariffLocationState = location.state as ConditionsAndTariffLocationState;

  const submitForm = async (values: any) => {
    const isManualTariff = values.isStandarTariff === ConditionsTariffEnum.MANUAL;

    if (isManualTariff && !hasValidManualValues(values)) {
      return triggerToasts('Completá todos los valores del tarifario manual con porcentajes válidos');
    }

    setIsLoading(true);

    const requestBody: UpdateConditionsAndTariffsRequest = {
      conditions: {
        averageMonthlyBilling: values.averageMonthlyBilling,
        currency: values.currency,
        anticipatePayments: values.anticipatePayments === 'true' ? true : false,
        surchargedFee: false,
      },
      tariff: {
        id: localState.tariff?.id ? Number(localState.tariff?.id) : undefined,
        standard: values.isStandarTariff === ConditionsTariffEnum.STANDAR,
        tariffType: TARIFF_TYPE_BY_SELECTION[values.isStandarTariff as ConditionsTariffEnum],
        exceedsAnnualBilling:
          values.exceedsAnnualBilling !== undefined && values.exceedsAnnualBilling !== ''
            ? values.exceedsAnnualBilling === 'true'
            : undefined,
        master: {
          debit: addPercentage(values.master[0].rows[0].currency),
          prepaid: addPercentage(values.master[0].rows[1].currency),
          cashCredit: addPercentage(values.master[0].rows[2].currency),
          installmentCredit: addPercentage(values.master[0].rows[3].currency),
          foreignDebit: addPercentage(values.master[0].rows[4].currency),
          foreignCredit: addPercentage(values.master[0].rows[5].currency),
        },
        visa: {
          debit: addPercentage(values.visa[0].rows[0].currency),
          prepaid: addPercentage(values.visa[0].rows[1].currency),
          cashCredit: addPercentage(values.visa[0].rows[2].currency),
          installmentCredit: addPercentage(values.visa[0].rows[3].currency),
          foreignDebit: addPercentage(values.visa[0].rows[4].currency),
          foreignCredit: addPercentage(values.visa[0].rows[5].currency),
        },
        hasRestriction: managerHasTariffRestriction,
        visaRateType: values.visaRateType,
        masterRateType: values.masterRateType,
      },
      acceptVisa: true,
      acceptMaster: true,
    };
    const response = await updateConditionsTariff(localState?.id!, requestBody);
    setIsLoading(false);

    if (response?.result && response?.data) {
      const nextScreen = pagesPaths.CommerceTerminals;
      return navigate(nextScreen, {
        state: { ...localState, ...response.data.businessData, from: pagesPaths.CommerceConditions },
      });
    } else {
      triggerToasts(response);
    }
  };

  return (
    <>
      <Container className="conditions-and-tariff">
        <ProgressBar now={getProgressPercentage('CommerceConditions')} />
        <h1>
          <img src={IssuerLogo} alt="issuer-logo" className="me-3" /> Condiciones/Tarifario
        </h1>
        <br />
        <ConditionsForm
          localState={localState}
          formRef={formRef}
          handleSubmit={submitForm}
          managerHasTariffRestriction={managerHasTariffRestriction}
        />
      </Container>
      <ConditionsFooter localState={localState} formRef={formRef} isLoading={isLoading} />
    </>
  );
}

type BrandTableData = {
  visa: CustomTableChunk[];
  master: CustomTableChunk[];
};

const cloneChunks = (chunks: CustomTableChunk[]) =>
  chunks.map((chunk) => ({ ...chunk, rows: chunk.rows.map((row) => ({ ...row })) }));

const rateTypeLabel = (selectedTariff: ConditionsTariffEnum) =>
  selectedTariff === ConditionsTariffEnum.MANUAL ? 'Arancel base' : 'Arancel personalizado';

const TARIFF_TYPE_BY_SELECTION: Record<ConditionsTariffEnum, TariffType> = {
  [ConditionsTariffEnum.STANDAR]: TariffType.STANDARD,
  [ConditionsTariffEnum.PERSONALIZED]: TariffType.PERSONALIZED,
  [ConditionsTariffEnum.MANUAL]: TariffType.MANUAL,
};

/** Falls back to `standard` in case the backend hasn't stored the type yet (e.g. pre-migration). */
const getSavedTariffType = (localState: ConditionsAndTariffLocationState): TariffType | null => {
  if (localState.tariffType) return localState.tariffType;
  if (localState.standard === false && localState.tariff) return TariffType.PERSONALIZED;
  if (localState.standard === true) return TariffType.STANDARD;
  return null;
};

/** The saved tariff is only preselected when the executive can use its type; otherwise standard. */
const getInitialTariffSelection = (
  savedTariffType: TariffType | null,
  canAccessPersonalizedTariff: boolean,
  canUseManualTariff: boolean,
): ConditionsTariffEnum => {
  if (savedTariffType === TariffType.MANUAL && canUseManualTariff) return ConditionsTariffEnum.MANUAL;
  if (savedTariffType === TariffType.PERSONALIZED && canAccessPersonalizedTariff) {
    return ConditionsTariffEnum.PERSONALIZED;
  }
  return ConditionsTariffEnum.STANDAR;
};

// Stays open until dismissed: saving the standard tariff replaces the saved one.
const warnSavedTariffUnavailable = () =>
  toast.warning(
    'La tarifa guardada de este comercio no está disponible para tu usuario, por lo que se seleccionó la tarifa Estándar. Si guardás, se reemplaza la tarifa anterior.',
    // Fixed id so a re-mount (e.g. React StrictMode in development) doesn't show it twice.
    { autoClose: false, toastId: 'saved-tariff-unavailable' },
  );

const toTariffNumber = (value: string | number) => Number(String(value).replace('%', '').replace(',', '.'));

const hasValidManualValues = (values: any) =>
  [...values.master, ...values.visa].every((chunk: CustomTableChunk) =>
    chunk.rows.every(
      (row) =>
        String(row.currency).trim() !== '' &&
        Number.isFinite(toTariffNumber(row.currency)) &&
        toTariffNumber(row.currency) >= 0,
    ),
  );

function ExceedsAnnualBillingSync({
  sectorTableData,
  setPersonalizedTariffsData,
  setPersonalizedTariffs,
}: {
  sectorTableData: BusinessSectorTableResponse | null;
  setPersonalizedTariffsData: React.Dispatch<React.SetStateAction<BrandTableData>>;
  setPersonalizedTariffs: React.Dispatch<React.SetStateAction<BrandTableData>>;
}) {
  const { values, setFieldValue } = useFormikContext<any>();

  useEffect(() => {
    if (
      !sectorTableData ||
      values.isStandarTariff !== ConditionsTariffEnum.PERSONALIZED ||
      values.exceedsAnnualBilling === undefined ||
      values.exceedsAnnualBilling === ''
    )
      return;

    const source =
      values.exceedsAnnualBilling === 'true' ? sectorTableData.exceeds : sectorTableData.standard;
    const baseData = { master: cloneChunks(source.master), visa: cloneChunks(source.visa) };
    const data = getPersonalizedTariffsFromSector(
      sectorTableData,
      values.exceedsAnnualBilling,
      values.masterRateType ?? TariffRateType.SUGGESTED,
      values.visaRateType ?? TariffRateType.SUGGESTED,
    );

    setPersonalizedTariffsData(baseData);
    setPersonalizedTariffs(data);
    setFieldValue('master', data.master);
    setFieldValue('visa', data.visa);
  }, [values.exceedsAnnualBilling, sectorTableData]);

  return null;
}

type ManualSeed = { exceedsAnnualBilling: string; masterRateType: string; visaRateType: string };

/**
 * A manual tariff starts from the personalized rates for the selected annual billing answer and
 * rate types, then the executive edits them freely. Changing a base answer reseeds the affected
 * brand. When reopening a commerce saved with a manual tariff, its saved rates are restored once
 * the sector table arrives (it provides the table rows they are loaded into).
 */
function ManualTariffSync({
  sectorTableData,
  savedTariff,
  restoreSavedManualTariff,
}: {
  sectorTableData: BusinessSectorTableResponse | null;
  savedTariff: ConditionsAndTariffLocationState['tariff'];
  restoreSavedManualTariff: boolean;
}) {
  const { values, setFieldValue } = useFormikContext<any>();
  const lastSeed = useRef<ManualSeed | null>(null);
  const previousSelectedTariff = useRef<string>(values.isStandarTariff);
  const hasCheckedSavedTariff = useRef(false);

  useEffect(() => {
    if (!sectorTableData || hasCheckedSavedTariff.current) return;
    hasCheckedSavedTariff.current = true;

    // Skip it if the executive already switched to another tariff before the table arrived.
    if (!restoreSavedManualTariff || values.isStandarTariff !== ConditionsTariffEnum.MANUAL) return;

    const seed: ManualSeed = {
      exceedsAnnualBilling: savedTariff?.exceedsAnnualBilling ? 'true' : 'false',
      masterRateType: values.masterRateType ?? TariffRateType.SUGGESTED,
      visaRateType: values.visaRateType ?? TariffRateType.SUGGESTED,
    };
    const base = getPersonalizedTariffsFromSector(
      sectorTableData,
      seed.exceedsAnnualBilling,
      seed.masterRateType as TariffRateType,
      seed.visaRateType as TariffRateType,
    );
    const saved = parsePersonalizedTariffsSentToNumbers(savedTariff, base);

    lastSeed.current = seed;
    setFieldValue('exceedsAnnualBilling', seed.exceedsAnnualBilling);
    setFieldValue('master', saved.master);
    setFieldValue('visa', saved.visa);
  }, [sectorTableData]);

  useEffect(() => {
    const wasManual = previousSelectedTariff.current === ConditionsTariffEnum.MANUAL;
    previousSelectedTariff.current = values.isStandarTariff;

    if (values.isStandarTariff !== ConditionsTariffEnum.MANUAL) {
      if (wasManual) lastSeed.current = null;
      return;
    }
    if (!sectorTableData || !values.exceedsAnnualBilling) return;

    const seed: ManualSeed = {
      exceedsAnnualBilling: values.exceedsAnnualBilling,
      masterRateType: values.masterRateType ?? TariffRateType.SUGGESTED,
      visaRateType: values.visaRateType ?? TariffRateType.SUGGESTED,
    };
    const last = lastSeed.current;
    const reseedAll = last?.exceedsAnnualBilling !== seed.exceedsAnnualBilling;
    const reseedMaster = reseedAll || last?.masterRateType !== seed.masterRateType;
    const reseedVisa = reseedAll || last?.visaRateType !== seed.visaRateType;
    if (!reseedMaster && !reseedVisa) return;

    const base = getPersonalizedTariffsFromSector(
      sectorTableData,
      seed.exceedsAnnualBilling,
      seed.masterRateType as TariffRateType,
      seed.visaRateType as TariffRateType,
    );
    lastSeed.current = seed;
    if (reseedMaster) setFieldValue('master', base.master);
    if (reseedVisa) setFieldValue('visa', base.visa);
  }, [
    values.isStandarTariff,
    values.exceedsAnnualBilling,
    values.masterRateType,
    values.visaRateType,
    sectorTableData,
  ]);

  return null;
}

function ConditionsForm({
  localState,
  formRef,
  handleSubmit,
  managerHasTariffRestriction,
}: {
  localState: ConditionsAndTariffLocationState;
  formRef: React.MutableRefObject<FormikProps<any> | null>;
  handleSubmit: (values: any) => void;
  managerHasTariffRestriction: boolean;
}) {
  const [standardTariffsData, setStandardTariffsData] = useState<BrandTableData>({ master: [], visa: [] });
  const [sectorTableData, setSectorTableData] = useState<BusinessSectorTableResponse | null>(null);
  const [personalizedTariffsData, setPersonalizedTariffsData] = useState<BrandTableData>({
    master: personalizedTariffMasterDataDefault,
    visa: personalizedTariffVisaDataDefault,
  });

  const [personalizedTariffs, setPersonalizedTariffs] = useState<BrandTableData>({
    master: personalizedTariffMasterDataDefault,
    visa: personalizedTariffVisaDataDefault,
  });

  const businessSector =
    BusinessSectorEnum[getEnumTypeValue('businessCategory', localState.businessCategory!)];
  let canAccessPersonalizedTariff = checkCanAccessPersonalizedTariff(
    businessSector,
    managerHasTariffRestriction,
  );
  const canUseManualTariff = checkCanUseManualTariff();
  const savedTariffType = getSavedTariffType(localState);
  const initialTariffSelection = getInitialTariffSelection(
    savedTariffType,
    canAccessPersonalizedTariff,
    canUseManualTariff,
  );
  const isSavedTariffUnavailable =
    savedTariffType !== null &&
    savedTariffType !== TariffType.STANDARD &&
    initialTariffSelection === ConditionsTariffEnum.STANDAR;

  useEffect(() => {
    if (isSavedTariffUnavailable) warnSavedTariffUnavailable();
  }, []);

  const { hasBankAccount, currencyToOperate } = getBankAccountsCurrencyToOperate(
    localState.bankAccounts ?? [],
  );

  const handleChangeTariff = (value: string, setFieldValue: any) => {
    if (value === ConditionsTariffEnum.STANDAR) {
      setFieldValue('isStandarTariff', ConditionsTariffEnum.STANDAR);
      setFieldValue('visa', standardTariffsData.visa);
      setFieldValue('master', standardTariffsData.master);
    } else if (value === ConditionsTariffEnum.MANUAL) {
      // ManualTariffSync seeds the editable rates from the personalized ones.
      setFieldValue('isStandarTariff', ConditionsTariffEnum.MANUAL);
    } else {
      setFieldValue('isStandarTariff', ConditionsTariffEnum.PERSONALIZED);
      setFieldValue('visa', personalizedTariffs.visa);
      setFieldValue('master', personalizedTariffs.master);
    }
  };

  useEffect(() => {
    const getTableData = async () => {
      const response = await getBusinessPlansTableData(
        localState.taxpayerType?.id.toString() || '',
        localState.businessCategory?.name.toUpperCase() || '',
      );

      if (response.result) {
        setStandardTariffsData({
          visa: formatTableDataForTariffsVisa(response.data),
          master: formatTableDataForTariffsMaster(response.data),
        });
      }
    };

    void getTableData();
  }, [localState.businessCategory, localState.taxpayerType]);

  const fetchPersonalizedTariffValues = async (businessSector: string) => {
    const serviceResponse = await businessSectorService.getBusinessSectorTable(businessSector);

    setSectorTableData(serviceResponse);
    setPersonalizedTariffsData({
      visa: cloneChunks(serviceResponse.standard.visa),
      master: cloneChunks(serviceResponse.standard.master),
    });
  };

  useEffect(() => {
    void fetchPersonalizedTariffValues(localState.businessCategory.name);
  }, [localState.businessCategory.name]);

  const handleChangeManualTable = (
    event: ChangeEvent<HTMLInputElement>,
    tableData: CustomTableChunk[],
    setFieldValue: (field: string, value: CustomTableChunk[]) => void,
    field: string,
  ) => {
    const rowIndex = Number(event.target.id.split('_')[1]);
    const newTableData = tableData.map((chunk) => ({
      ...chunk,
      rows: chunk.rows.map((row, index) =>
        index === rowIndex ? { ...row, currency: event.target.value } : row,
      ),
    }));

    setFieldValue(field, newTableData);
  };

  // Until the sector table arrives and the billing question is answered, the form still holds
  // placeholder rates, which must never be submitted as a manual tariff.
  const handleSubmitConditions = (values: any) => {
    if (
      values.isStandarTariff === ConditionsTariffEnum.MANUAL &&
      (!sectorTableData || !values.exceedsAnnualBilling)
    ) {
      return triggerToasts('Completá la facturación anual para cargar el tarifario manual');
    }
    handleSubmit(values);
  };

  return (
    <Formik
      innerRef={(formElement) => (formRef.current = formElement)}
      validationSchema={schemas.CommerceConditionsSchema}
      onSubmit={handleSubmitConditions}
      initialValues={{
        isStandarTariff: initialTariffSelection,
        currency: hasBankAccount ? currencyToOperate : (localState?.currency || currencyToOperate || ''),
        visa: personalizedTariffsData.visa,
        master: personalizedTariffsData.master,
        anticipatePayments:
          localState?.anticipatePayments !== null
            ? localState?.anticipatePayments === true
              ? 'true'
              : 'false'
            : localState?.taxpayerType?.description === smallBusinessDescription
              ? 'true'
              : undefined,
        visaRateType:
          !localState.standard && localState.tariff
            ? localState.tariff.visaRateType
            : TariffRateType.SUGGESTED,
        masterRateType:
          !localState.standard && localState.tariff
            ? localState.tariff.masterRateType
            : TariffRateType.SUGGESTED,
        exceedsAnnualBilling:
          !localState.standard && localState.tariff
            ? (localState.tariff as { exceedsAnnualBilling?: boolean }).exceedsAnnualBilling === true
              ? 'true'
              : (localState.tariff as { exceedsAnnualBilling?: boolean }).exceedsAnnualBilling === false
                ? 'false'
                : (undefined as string | undefined)
            : (undefined as string | undefined),
      }}
    >
      {({ values, setFieldValue }) => (
        <Form className="row">
          <ExceedsAnnualBillingSync
            sectorTableData={sectorTableData}
            setPersonalizedTariffsData={setPersonalizedTariffsData}
            setPersonalizedTariffs={setPersonalizedTariffs}
          />
          <ManualTariffSync
            sectorTableData={sectorTableData}
            savedTariff={localState.tariff}
            restoreSavedManualTariff={initialTariffSelection === ConditionsTariffEnum.MANUAL}
          />
          <Select
            name="currency"
            label="Moneda"
            className="col-md-6"
            noPlaceholder={localState?.accountTrackingNumber === ''}
            required
            disabled={hasBankAccount}
          >
            {currencies?.map((currency, index) => (
              <option key={index} value={currency.value}>
                {capitalize(currency.name)}
              </option>
            ))}
          </Select>
          <AnticipatePaymentsSelect />
          <div className="col-md-12 mt-2 mb-4">
            <RoundedCheckbox
              name="isStandarTariff"
              label="Estándar"
              onChange={handleChangeTariff}
              setFieldValue={setFieldValue}
              value={ConditionsTariffEnum.STANDAR}
              currentComboCheckboxValue={values.isStandarTariff}
            />
            {canAccessPersonalizedTariff && (
              <RoundedCheckbox
                name="isStandarTariff"
                label="Personalizado"
                onChange={handleChangeTariff}
                setFieldValue={setFieldValue}
                value={ConditionsTariffEnum.PERSONALIZED}
                currentComboCheckboxValue={values.isStandarTariff}
              />
            )}
            {canUseManualTariff && (
              <RoundedCheckbox
                name="isStandarTariff"
                label="Manual"
                onChange={handleChangeTariff}
                setFieldValue={setFieldValue}
                value={ConditionsTariffEnum.MANUAL}
                currentComboCheckboxValue={values.isStandarTariff}
              />
            )}
          </div>
          {values.isStandarTariff === ConditionsTariffEnum.STANDAR ? (
            <>
              <p className="processor-title">VISA</p>
              <CustomTable headings={standarComisionHeadings} tableData={standardTariffsData.visa} />

              <p className="processor-title">MASTER</p>
              <CustomTable headings={standarComisionHeadings} tableData={standardTariffsData.master} />
            </>
          ) : (
            <>
              {values.isStandarTariff === ConditionsTariffEnum.PERSONALIZED && (
                <div className="col-12 mb-3">
                  <p className="tariff-disclaimer">
                    El arancel personalizado ya no admite valores ingresados manualmente. Debes seleccionar un
                    valor entre el arancel Sugerido o Mínimo
                  </p>
                </div>
              )}
              <Select
                name="exceedsAnnualBilling"
                label="¿La facturación anual es mayor a USD 250.000?"
                required
                className="col-12 md-3"
              >
                <option value="true">Si</option>
                <option value="false">No</option>
              </Select>
              {values.exceedsAnnualBilling !== undefined && values.exceedsAnnualBilling !== '' && (
                <>
                  <Select
                    name="masterRateType"
                    label={`${rateTypeLabel(values.isStandarTariff)} Propuesta MASTER`}
                    required
                    className="col-md-6"
                  >
                    <option value={TariffRateType.SUGGESTED}>Sugerido</option>
                    <option value={TariffRateType.MINIMUM}>Mínimo</option>
                  </Select>
                  <Select
                    name="visaRateType"
                    label={`${rateTypeLabel(values.isStandarTariff)} Propuesta VISA`}
                    required
                    className="col-md-6"
                  >
                    <option value={TariffRateType.SUGGESTED}>Sugerido</option>
                    <option value={TariffRateType.MINIMUM}>Mínimo</option>
                  </Select>
                  {values.isStandarTariff === ConditionsTariffEnum.MANUAL ? (
                    <>
                      <p className="processor-title">Propuesta MASTER</p>
                      <PersonalaizedTariffTable
                        tableData={values.master}
                        headings={masterManualComisionHeadings}
                        field="master"
                        setFieldValue={setFieldValue}
                        handleChangePersonalaizedTable={handleChangeManualTable}
                      />
                      <p className="processor-title">Propuesta VISA</p>
                      <PersonalaizedTariffTable
                        tableData={values.visa}
                        headings={visaManualComisionHeadings}
                        field="visa"
                        setFieldValue={setFieldValue}
                        handleChangePersonalaizedTable={handleChangeManualTable}
                      />
                    </>
                  ) : (
                    <PersonalizedTariffsSection
                      personalizedTariffs={
                        sectorTableData
                          ? getPersonalizedTariffsFromSector(
                              sectorTableData,
                              values.exceedsAnnualBilling,
                              values.masterRateType ?? TariffRateType.SUGGESTED,
                              values.visaRateType ?? TariffRateType.SUGGESTED,
                            )
                          : personalizedTariffs
                      }
                      setPersonalizedTariffs={setPersonalizedTariffs}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Form>
      )}
    </Formik>
  );
}

function PersonalizedTariffsSection({
  personalizedTariffs,
  setPersonalizedTariffs,
}: {
  personalizedTariffs: BrandTableData;
  setPersonalizedTariffs: React.Dispatch<React.SetStateAction<BrandTableData>>;
}) {
  const { values, setFieldValue } = useFormikContext<any>();

  useEffect(() => {
    const { master, visa } = updatePersonalizedTariffs(
      personalizedTariffs,
      values.masterRateType,
      values.visaRateType,
    );
    setFieldValue('master', master);
    setFieldValue('visa', visa);
    setPersonalizedTariffs({ master, visa });
  }, [values.masterRateType, values.visaRateType]);

  return (
    <>
      <p className="processor-title">Propuesta MASTER</p>
      <CustomTable
        headings={masterPersonalizedComisionHeadings}
        tableData={personalizedTariffs.master.map((tableChunk) => ({
          ...tableChunk,
          rows: tableChunk.rows.map((row) => {
            const currencyFormatted =
              typeof row.currency === 'number'
                ? row.currency.toFixed(2).replace('.', ',')
                : String(row.currency).replace('.', ',');
            return { ...row, currency: addPercentage(currencyFormatted) };
          }),
        }))}
      />

      <p className="processor-title">Propuesta VISA</p>
      <CustomTable
        headings={visaPersonalizedComisionHeadings}
        tableData={personalizedTariffs.visa.map((tableChunk) => ({
          ...tableChunk,
          rows: tableChunk.rows.map((row) => {
            const currencyFormatted =
              typeof row.currency === 'number'
                ? row.currency.toFixed(2).replace('.', ',')
                : String(row.currency).replace('.', ',');
            return { ...row, currency: addPercentage(currencyFormatted) };
          }),
        }))}
      />
    </>
  );
}

function AnticipatePaymentsSelect() {
  const [teas, setTeas] = useState<{ tea: string; usdTea: string } | null>(null);

  const fetchTeas = async () => {
    const valuesConfiguration = await valuesConfigurationService.getValuesConfigurations();
    const tea = valuesConfiguration.find(({ name }) => name === ValuesConfigurationName.TEA);
    const usdTea = valuesConfiguration.find(({ name }) => name === ValuesConfigurationName.USD_TEA);

    setTeas({ tea: String(tea?.value!), usdTea: String(usdTea?.value!) });
  };

  useEffect(() => {
    if (!!teas) return;
    void fetchTeas();
  }, [teas]);

  return (
    <Select
      className="col-md-6"
      name="anticipatePayments"
      label={`Solicito anticipar todos mis pagos a 24hs. TEA: ${teas?.tea}% TEA y U$S (Dólares) TEA: ${teas?.usdTea}%`}
      required
    >
      <BooleanishOptions />
    </Select>
  );
}

function ConditionsFooter({
  localState,
  formRef,
  isLoading,
}: {
  localState: ConditionsAndTariffLocationState;
  formRef: React.RefObject<FormikProps<any>>;
  isLoading: boolean;
}) {
  const navigate = useNavigate();

  const handleGoBack = () => navigate(pagesPaths.CommerceBankAccount, { state: { ...localState } });

  const handleSubmit = () => formRef.current?.handleSubmit();

  return <NavigationFooter previousHandler={handleGoBack} nextHandler={handleSubmit} loading={isLoading} />;
}

export default CommerceConditions;
